import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { VW_ERROR_CODES, VwApiRequestsService, VwApiResponse, VwAuthService } from 'vwappservices';
import { SessionExpiredModal } from '../shared/session-expired-modal/session-expired-modal';
import { API } from './api-endpoints.constants';
import { ApiResult, normalizeResponse } from './api-requests.service';
import { UserInfo } from './field-keys.constants';
import { LoaderService } from './loader.service';
import { UserSessionService } from './user-session.service';

interface SessionData {
  userInfo: UserInfo;
  functionalities: string[];
}

/** Login, logout, session restore after reload and session-expiry handling (on top of VwAuthService). */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly vwAuth = inject(VwAuthService);
  private readonly vwApi = inject(VwApiRequestsService);
  private readonly session = inject(UserSessionService);
  private readonly loader = inject(LoaderService);
  private readonly router = inject(Router);
  private readonly modal = inject(NgbModal);

  private expiryDialogOpen = false;

  /** Registered once at startup: vwappservices calls it when a token cannot be refreshed. */
  registerUnauthorisedHandler(): void {
    this.vwApi.setUnauthorisedHandler(() => void this.handleSessionExpired());
  }

  async login(loginId: string, password: string): Promise<ApiResult<SessionData>> {
    return this.completeLogin(await this.vwAuth.login(API.AUTH.LOGIN, { loginId: loginId.trim(), password }));
  }

  /** vwAuth.login stores the tokens on success; this sets the app session when the response carries one. */
  private completeLogin(raw: VwApiResponse, fallback = 'Login failed.'): ApiResult<SessionData> {
    const res = normalizeResponse(raw);
    const ok = res.errorCode === VW_ERROR_CODES.SUCCESS;
    if (ok && res.Data?.userInfo) this.session.setSession(res.Data.userInfo, res.Data.functionalities ?? []);
    return { ok, data: res.Data, errorCode: res.errorCode, message: res.errorDescription || (ok ? '' : fallback) };
  }

  /**
   * Runs before the first navigation. Uses the httpOnly refresh cookie to get a new access token,
   * then loads the user + functionalities. Returns false when the user must log in.
   */
  async restoreSession(): Promise<boolean> {
    try {
      if (!(await this.vwAuth.restoreSession())) return false;
      const res = normalizeResponse(await this.vwApi.postAPICall(API.AUTH.SESSION, {}));
      if (res.errorCode !== VW_ERROR_CODES.SUCCESS) return false;
      this.session.setSession(res.Data.userInfo, res.Data.functionalities);
      return true;
    } catch {
      return false;
    }
  }

  async logout(): Promise<void> {
    this.loader.show('Signing out...');
    try {
      await this.vwAuth.logout(API.AUTH.LOGOUT);
    } catch {
      /* tokens are cleared locally regardless */
    } finally {
      this.loader.hide();
    }
    this.endSession();
  }

  private async handleSessionExpired(): Promise<void> {
    if (!this.session.isLoggedIn() || this.expiryDialogOpen) {
      this.endSession();
      return;
    }
    this.expiryDialogOpen = true;
    this.modal.dismissAll();
    this.loader.reset();
    const ref = this.modal.open(SessionExpiredModal, { backdrop: 'static', keyboard: false, centered: true });
    await ref.result.catch(() => undefined);
    this.expiryDialogOpen = false;
    this.endSession();
  }

  private endSession(): void {
    this.session.clear();
    this.modal.dismissAll();
    void this.router.navigate(['/login']);
  }
}
